import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { MessageCircle } from 'lucide-react'
import { waApi } from '../../api/whatsapp'
import { leadsApi } from '../../api/leads'
import { DropdownSelect } from '../../components/ui/DropdownSelect'
import { ConversationPane } from './ConversationPane'

interface Props {
  companyId: string
  leadId: string
}

export function WhatsAppLeadTab({ companyId, leadId }: Props) {
  const qc = useQueryClient()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [startSessionId, setStartSessionId] = useState('')
  const dismissed = useRef(false) // user closed the pane (e.g. marked unread) — don't auto-reselect

  const { data: chats = [], isPending } = useQuery({
    queryKey: ['wa-lead-chats', companyId, leadId],
    queryFn: () => waApi.leadChats(companyId, leadId),
    refetchInterval: 8_000,
  })
  const { data: sessions = [] } = useQuery({
    queryKey: ['wa-sessions', companyId],
    queryFn: () => waApi.listSessions(companyId),
  })
  const { data: lead } = useQuery({
    queryKey: ['lead', companyId, leadId],
    queryFn: () => leadsApi.get(companyId, leadId).then(r => r.data),
  })

  const connected = sessions.filter(s => s.status === 'connected')

  // Auto-select the most recent chat once (unless the user dismissed the pane).
  useEffect(() => {
    if (!selectedId && !dismissed.current && chats.length) setSelectedId(chats[0].id)
  }, [chats, selectedId])
  useEffect(() => { setStartSessionId(connected[0]?.id ?? '') }, [connected.length])

  const start = useMutation({
    mutationFn: () => waApi.startChat(companyId, { sessionId: startSessionId, leadId }),
    onSuccess: (chat) => {
      dismissed.current = false
      qc.invalidateQueries({ queryKey: ['wa-lead-chats', companyId, leadId] })
      setSelectedId(chat.id)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to start chat'),
  })

  const sessionLabel = (sid: string) => {
    const s = sessions.find(x => x.id === sid)
    return s ? `${s.name}${s.phoneNumber ? ` (${s.phoneNumber})` : ''}` : 'WhatsApp'
  }

  const selectedChat = chats.find(c => c.id === selectedId)

  if (isPending) return <div className="p-6 text-center text-sm text-gray-400">Loading…</div>

  // No chat yet — offer to start one (or explain why we can't).
  if (chats.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
        <MessageCircle className="h-8 w-8 text-gray-300" />
        {connected.length === 0 ? (
          <p className="text-sm text-gray-500">No WhatsApp number is connected. Owners can connect one in Settings → WhatsApp.</p>
        ) : !lead?.phone ? (
          <p className="text-sm text-gray-500">Add a phone number to this lead to start a WhatsApp chat.</p>
        ) : (
          <>
            <p className="text-sm text-gray-500">No WhatsApp conversation with this lead yet.</p>
            {connected.length > 1 && (
              <div className="w-64">
                <DropdownSelect
                  value={startSessionId}
                  onChange={setStartSessionId}
                  options={connected.map(s => ({ value: s.id, label: sessionLabel(s.id) }))}
                />
              </div>
            )}
            <button className="btn btn-primary btn-sm" disabled={!startSessionId || start.isPending} onClick={() => start.mutate()}>
              Start WhatsApp chat
            </button>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="p-3">
      {chats.length > 1 && (
        <div className="mb-2">
          <label className="label">Conversation</label>
          <DropdownSelect
            value={selectedId ?? ''}
            onChange={id => { dismissed.current = false; setSelectedId(id) }}
            options={chats.map(c => ({ value: c.id, label: `via ${sessionLabel(c.sessionId)}` }))}
          />
        </div>
      )}
      {selectedChat ? (
        <div className="flex h-[560px] overflow-hidden rounded-lg border border-gray-200">
          <ConversationPane
            key={selectedChat.id}
            companyId={companyId}
            chatId={selectedChat.id}
            hideDetails
            onToggleDetails={() => {}}
            onClose={() => { dismissed.current = true; setSelectedId(null) }}
          />
        </div>
      ) : (
        <div className="flex h-[560px] flex-col items-center justify-center gap-2 rounded-lg border border-gray-200 text-sm text-gray-400">
          <span>Conversation closed.</span>
          <button className="btn btn-ghost btn-sm" onClick={() => { dismissed.current = false; setSelectedId(chats[0].id) }}>
            Reopen
          </button>
        </div>
      )}
    </div>
  )
}
